<template>
  <v-select
    v-model="selectedFilters"
    label="Search Filters"
    :items="filters"
    :disabled="disabled"
    item-text="name"
    item-value="key"
    background-color="input"
    style="border-top-right-radius: 0px; border-bottom-right-radius: 0px; border-right: 0px;"
    :menu-props="{ offsetY: true, maxHeight: '600' }"
    multiple
    outlined
    hide-details
    small-chips
    dense>
    <template v-slot:selection="{ attrs, item }">
      <v-chip
        v-bind="attrs"
        small>
        {{ getFilterText(item) }}
        <v-icon
          small
          right
          @click="removeFilter(item.key, true)">
          $delete
        </v-icon>
      </v-chip>
    </template>
    <template v-slot:item="{ item, attrs }">
      <v-list-item>
        <v-list-item-content>
          <v-autocomplete
            v-if="item.key=='store_id'"
            v-model="params[`${item.key}`]"
            :items="stores"
            :label="item.name"
            hide-details
            background-color="input"
            item-text="name"
            item-value="id"
            :type="item.type"
            dense
            clearable
            @change="handleInputValue(item)"
            @click:clear="removeFilter(item.key)"
            outlined>
          </v-autocomplete>
          <v-menu
            v-else-if="item.key=='effective_date_range'"
            v-model="menu"
            transition="scale-transition"
            offset-y
            :close-on-content-click="false"
            nudge-top="25"
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                v-on="on"
                :value="formattedDates"
                outlined
                :label="item.name"
                prepend-inner-icon="mdi-calendar"
                dense
                background-color="#fff"
                clearable
                @change="handleInputValue(item)"
                @click:clear="removeFilter(item.key)">
              </v-text-field>
            </template>
            <v-date-picker
              v-model="params[`${item.key}`]"
              range
              show-current
              :min=" params.effective_date_range ? params.effective_date_range[0] : null"
              scrollable
              @change="handleInputValue(item)"
              no-title>
            </v-date-picker>
          </v-menu>
          <v-text-field
            v-else
            v-model="params[`${item.key}`]"
            :label="item.name"
            hide-details
            background-color="input"
            :prefix="item.prefix || null"
            :type="item.type"
            dense
            clearable
            @keyup.stop="attrs.inputValue ? null : handleInputValue(item)"
            @click:clear="removeFilter(item.key)"
            outlined>
          </v-text-field>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-select>
</template>
<script>
// mixins
import { userAccess } from '@/mixins/user-access'
// third-party
import { debounce } from 'lodash'
export default {
  data () {
    return {
      selectedFilters: [],
      filters: [
        { name: 'Store', key: 'store_id', type: 'text' },
        { name: 'Item Id', key: 'item_id', type: 'text' },
        { name: 'UPC', key: 'upc', type: 'text' },
        { name: 'Description', key: 'description', type: 'text' },
        { name: 'Effective Date Range', key: 'effective_date_range', type: 'date' }
      ],
      params: {
        store_id: '',
        description: '',
        item_id: '',
        upc: '',
        effective_date_range: null
      },
      menu: false
    }
  },
  name: 'PriceSearchFilters',
  mixins: [userAccess],
  props: {
    value: Object,
    disabled: Boolean
  },
  watch: {
    activeFilters: {
      handler (newValue) {
        if (newValue) {
          this.updateSearchFilters()
        }
      },
      deep: true
    }
  },
  created () {
    if (this.value) {
      this.params = { ...this.params, ...this.value }
      if (!this.params.store_id && this.isRetailer) {
        if (this.stores.length > 0) {
          this.params.store_id = this.stores[0].id
        }
      }
      const activeKeys = Object.keys(this.params).filter(key => Boolean(this.params[key]))
      if (activeKeys.length > 0) this.selectedFilters.push(...activeKeys)
    }
  },
  computed: {
    stores () {
      return this.userStores
    },
    activeFilters () {
      return Object.keys(this.params).filter(this.isActive)
    },
    formattedDates() {
      if (this.params.effective_date_range && this.params.effective_date_range.length > 0) {
        const formattedDates = this.params.effective_date_range.map(date => {
          const [year, month, day] = date.split('-')
          return `${month}/${day}/${year}`
        })
        return formattedDates.join(' - ')
      }
      return null
    }
  },
  methods: {
    isActive (key) {
      if (this.isSelected(key) && Boolean(this.params[key])) {
        const value = this.params[key]
        return Array.isArray(value) ? (value.length > 0) : true
      }
      return false
    },
    getFilterText (item) {
      if (item.key === 'store_id') {
        const storeId = this.params.store_id
        const store = this.stores.find(store => store && store.id === storeId)
        const storeName = store ? store.name : ''
        return `${item.name}: ${storeName}`
      } else if (item.key === 'effective_date_range') {
        return `${item.name}: ${this.formattedDates}`
      } else {
          return `${item.text || item.name}: "${this.params[item.key]}"`
      }
    },
    removeFilter (key, clearValue = false) {
      const index = this.selectedFilters.indexOf(key)
      if (index > -1) {
        this.selectedFilters.splice(index, 1)
        if (clearValue) this.params[key] = null
      }
    },
    handleInputValue: debounce(function (item) {
      if (Boolean(this.params[item.key]) && !this.isSelected(item.key)) {
        this.selectedFilters.push(item.key)
      }
    }, 200),
    isSelected (key) {
      return this.selectedFilters.includes(key)
    },
    updateSearchFilters () {
      const filtersObj = this.activeFilters.reduce((obj, key) => {
        obj[key] = this.params[key]
        return obj
      }, {})
      this.$emit('input', filtersObj)
      if (this.activeFilters.length > 0) {
        sessionStorage.setItem('price_search_filters', JSON.stringify(filtersObj))
      } else {
        sessionStorage.removeItem('price_search_filters')
      }
    }
  }
}
</script>


// WEBPACK FOOTER //
// src/components/pricing/PriceSearchFilters.vue